import { ICustomer } from '@/shared/model/customer.model';

export default class AppUtils {
  public isTablet() {
    const ua = window.navigator.userAgent.toLowerCase();
    return ua.indexOf('ipad') > -1 || (ua.indexOf('macintosh') > -1 && 'ontouchend' in document);
  }

  public toArray<T>(value: T | T[]): T[] {
    return Array.isArray(value) ? value : [value];
  }

  public debounce<T extends unknown[], U>(callback: (...args: T) => PromiseLike<U> | U, wait: number): any {
    let state:
      | undefined
      | {
          timeout: ReturnType<typeof setTimeout>;
          promise: Promise<U>;
          resolve: (value: U | PromiseLike<U>) => void;
          reject: (value: any) => void;
          latestArgs: T;
        } = undefined;

    return (...args: T): Promise<U> => {
      if (!state) {
        state = {} as any;
        state.promise = new Promise((resolve, reject) => {
          state.resolve = resolve;
          state.reject = reject;
        });
      }
      clearTimeout(state?.timeout);
      state.latestArgs = args;
      state.timeout = setTimeout(() => {
        const s = state;
        state = undefined;
        try {
          s.resolve(callback(...s.latestArgs));
        } catch (e) {
          s.reject(e);
        }
      }, wait);

      return state?.promise;
    };
  }

  public checkCustomerValid(customer: ICustomer) {
    let fieldMap = {
      activeCalculationParameters: 'Berechnungsparameter',
      'activeCalculationParameters.owner': 'Eigentümer',
      'activeCalculationParameters.calculationValue': 'Wert für Kalkulation',
      'activeCalculationParameters.residenceTime': 'Wohnzeit',
      'activeCalculationParameters.sellDecision': 'Verkauf+Wohnen',
      'activeCalculationParameters.payoutType': 'Auszahlungswunsch',
      firstPerson: 'Person',
      'firstPerson.firstname': 'Vorname',
      'firstPerson.lastname': 'Nachname',
      'firstPerson.age': 'Alter',
      'firstPerson.title': 'Anrede',
      'firstPerson.email': 'E-Mail',
      'firstPerson.phone': 'Telefon',
    };
    const completeFieldMap = {
      propertyData: 'Immobiliendaten',
      'propertyData.dossier': '',
      'propertyData.dossier.property': '',
      'propertyData.dossier.property.buildingYear': 'Baujahr',
      'propertyData.dossier.property.livingArea': 'Wohnfläche',
      'propertyData.dossier.property.condition': 'Zustand',
      'propertyData.dossier.property.propertyType': '',
      'propertyData.dossier.property.propertyType.code': 'Objekt-Typ',
      'propertyData.dossier.property.numberOfRooms': 'Anzahl Zimmer',
      'propertyData.dossier.property.numberOfBathrooms': 'Anzahl Badezimmer',
      'propertyData.dossier.property.landArea': 'Grundstücksfläche',
      'propertyData.dossier.property.numberOfUnits': 'Anzahl Wohneinheiten',
      'activeCalculationParameters.propertyCode OR propertyType': 'Immobiliencode oder -art',
    };

    const isCompleteAddress =
      customer != null &&
      customer.propertyData != null &&
      customer.propertyData.objectAddress != null &&
      customer.propertyData.objectAddress.zipcode != null &&
      customer.propertyData.objectAddress.city != null &&
      customer.propertyData.objectAddress.houseNumber != null &&
      customer.propertyData.objectAddress.latitude != null &&
      customer.propertyData.objectAddress.longitude != null &&
      customer.propertyData.objectAddress.street != null;

    if (isCompleteAddress) {
      fieldMap = {
        ...fieldMap,
        ...completeFieldMap,
      };
    }
    const requiredFields = Object.keys(fieldMap);

    const get = (object: any, path: string): any => {
      return path.split('.').reduce((obj, key) => (obj && obj[key] != null ? obj[key] : undefined), object);
    };

    const invalidFields: string[] = [];

    if (isCompleteAddress) {
      requiredFields.forEach(path => {
        if (
          [
            'propertyData.dossier.property.landArea',
            'propertyData.dossier.property.numberOfRooms',
            'propertyData.dossier.property.numberOfBathrooms',
            'propertyData.dossier.property.numberOfUnits',
          ].includes(path)
        )
          return;
        if (path === 'activeCalculationParameters.propertyCode OR propertyType') {
          const hasCode = get(customer, 'activeCalculationParameters.propertyCode') != null;
          const hasType = get(customer, 'propertyData.dossier.property.propertyType') != null;
          if (!(hasCode || hasType)) {
            invalidFields.push(path);
          }
        } else if (get(customer, path) == null) {
          invalidFields.push(path);
        }
      });

      const code = get(customer, 'propertyData.dossier.property.propertyType.code');
      const landArea = get(customer, 'propertyData.dossier.property.landArea');

      if (['house', 'multi_family_house'].includes(code ?? '') && (landArea == null || landArea == 0)) {
        invalidFields.push('propertyData.dossier.property.landArea');
      }

      const numberOfRooms = get(customer, 'propertyData.dossier.property.numberOfRooms');
      const numberOfBathrooms = get(customer, 'propertyData.dossier.property.numberOfBathrooms');

      if (['house', 'apartment'].includes(code ?? '')) {
        if (numberOfRooms == null || numberOfRooms == 0) {
          invalidFields.push('propertyData.dossier.property.numberOfRooms');
        }
        if (numberOfBathrooms == null || numberOfBathrooms == 0) {
          invalidFields.push('propertyData.dossier.property.numberOfBathrooms');
        }
      }

      const units = get(customer, 'propertyData.dossier.property.numberOfUnits');
      if (code === 'multi_family_house' && units == null) {
        invalidFields.push('propertyData.dossier.property.numberOfUnits');
      }
    }

    const uniqueFields: string[] = [];
    for (const field of invalidFields) {
      if (!uniqueFields.includes(field)) {
        uniqueFields.push(field);
      }
    }

    return {
      isValid: uniqueFields.length === 0,
      invalidFields: uniqueFields.map(key => fieldMap[key] || key),
    };
  }
}
